import React from "react";
import { agentStepsDone } from "./helpers";

/** Labels shown while the pipeline runs, in order. */
const PIPELINE_STEPS = [
  "Identifying company profile",
  "Identifying sector",
  "Finding comparable companies",
  "Selecting relevant ESG metrics",
  "Collecting latest sustainability disclosures",
  "Normalising ESG data",
  "Calculating peer benchmarks",
  "Scoring overall position",
];

/**
 * Shows the agent working while an analysis generates.
 *
 * @param {{run: object|null}} props - The run being generated.
 * @returns {JSX.Element|null} The overlay, or nothing when nothing is running.
 *
 * @remarks
 * The eight labels are presentation over three real section statuses, scaled so
 * progress never overtakes what the backend has actually reported. Results
 * render underneath as each section lands, so dismissing this reveals a
 * partially complete page rather than an empty one.
 */
const AgentProgressModal = ({ run }) => {
  if (run?.status !== "ANALYZING") return null;

  const done = agentStepsDone(run);
  const reached = Math.round((done / 6) * PIPELINE_STEPS.length);
  const percent = Math.round((reached / PIPELINE_STEPS.length) * 100);

  return (
    <div className="aica-overlay" role="dialog" aria-modal="true" aria-label="Analysis in progress">
      <div className="aica-progress-card">
        <h3>AI competitive intelligence agent</h3>
        <div className="aica-progress-sub">
          <span className="aica-pulse" />
          Analysing {run.baseCompanyName} against its sector peers…
        </div>

        <div className="aica-progress-track">
          <span className="aica-progress-fill" style={{ width: `${percent}%` }} />
        </div>
        <div className="aica-progress-percent">{percent}%</div>

        <div className="aica-progress-steps">
          {PIPELINE_STEPS.map((label, index) => {
            const isDone = index < reached;
            const isActive = index === reached;
            return (
              <div
                key={label}
                className={`aica-progress-step ${isDone ? "is-done" : ""} ${
                  isActive ? "is-active" : ""
                }`}
              >
                <span className="aica-progress-icon">{isDone ? "✓" : ""}</span>
                {label}
              </div>
            );
          })}
        </div>

        <p className="aica-progress-note">
          This takes a few minutes. Results appear as each section completes.
        </p>
      </div>
    </div>
  );
};

export default AgentProgressModal;
